import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Plus, Pencil, Trash2, Download, LogOut, Briefcase, Inbox, Users, X, Mail, ShieldCheck, ShieldAlert, RefreshCw, ChevronLeft, ChevronRight, Check, AlertTriangle, Clock, Send } from "lucide-react";
import { BrandLogo } from "@/components/LogoMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "adviserz_admin_token";

const authClient = () => {
  const c = axios.create({ baseURL: API });
  c.interceptors.request.use((cfg) => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    return cfg;
  });
  return c;
};

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setChecking(false); return; }
    authClient().get("/admin/me")
      .then(() => setAuthed(true))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!authed) return <Login onAuthed={() => setAuthed(true)} />;
  return <AdminDashboard onLogout={() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }} />;
}

function Login({ onAuthed }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuthed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-6 bg-background">
      <form onSubmit={submit} className="card-flat w-full max-w-sm p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-2 mb-6">
          <BrandLogo className="h-6" />
          <span className="text-xs text-muted-foreground ml-1">· admin</span>
        </div>
        <div className="label-xs mb-3 flex items-center gap-2"><Lock className="w-3 h-3" /> Restricted area</div>
        <label className="block">
          <div className="label-xs mb-2">Password</div>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required autoFocus data-testid="admin-password"
            className="w-full bg-background border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:border-ai" />
        </label>
        <button type="submit" disabled={loading} data-testid="admin-login-submit"
          className="w-full mt-4 px-5 py-3 rounded-md bg-white text-background font-semibold hover:bg-white/90 transition-colors disabled:opacity-60">
          {loading ? "Verifying…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("jobs");
  return (
    <div className="min-h-screen bg-background text-white">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo className="h-5" />
            <span className="text-xs text-muted-foreground ml-1">· admin</span>
          </div>
          <button onClick={onLogout} data-testid="admin-logout"
            className="text-xs text-muted-foreground hover:text-white flex items-center gap-1">
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 pt-8">
        <div className="flex gap-1 border-b border-border">
          {[
            { id: "jobs",         label: "Jobs",         icon: Briefcase },
            { id: "applications", label: "Applications", icon: Inbox },
            { id: "leads",        label: "Leads",        icon: Users },
            { id: "email_events", label: "Email events", icon: Mail },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                tab === t.id ? "border-ai text-white" : "border-transparent text-muted-foreground hover:text-white"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "jobs" && <JobsPanel />}
        {tab === "applications" && <ApplicationsPanel />}
        {tab === "leads" && <LeadsPanel />}
        {tab === "email_events" && <EmailEventsPanel />}
      </main>
    </div>
  );
}

function JobsPanel() {
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchJobs = useCallback(() => {
    authClient().get("/admin/jobs").then(r => setJobs(r.data)).catch(() => toast.error("Could not load jobs"));
  }, []);
  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const remove = async (id) => {
    if (!window.confirm("Delete this job and all its applications?")) return;
    try {
      await authClient().delete(`/admin/jobs/${id}`);
      toast.success("Job deleted");
      fetchJobs();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="label-xs mb-1">Job Postings</div>
          <h2 className="font-display font-bold text-2xl">{jobs.length} total</h2>
        </div>
        <button onClick={() => setCreating(true)} data-testid="create-job-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-background font-semibold hover:bg-white/90 transition-colors">
          <Plus className="w-4 h-4" /> New Job
        </button>
      </div>

      {jobs.length === 0 ? (
        <div className="card-flat p-10 text-center text-muted-foreground">No jobs yet. Create your first posting.</div>
      ) : (
        <ul className="space-y-3" data-testid="admin-jobs-list">
          {jobs.map((j) => (
            <li key={j.id} className="card-flat p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-lg">{j.title}</span>
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm ${j.is_active ? "bg-profit/20 text-profit" : "bg-muted/30 text-muted-foreground"}`}>
                    {j.is_active ? "Active" : "Hidden"}
                  </span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{j.department} · {j.location} · {j.employment_type}</div>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{j.description}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(j)} data-testid={`edit-job-${j.id}`}
                  className="px-3 py-2 rounded-md border border-border text-sm hover:bg-accent flex items-center gap-1">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => remove(j.id)} data-testid={`delete-job-${j.id}`}
                  className="px-3 py-2 rounded-md border border-border text-sm hover:bg-risk/20 hover:border-risk text-risk flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {(creating || editing) && (
        <JobForm job={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); fetchJobs(); }} />
      )}
    </div>
  );
}

function JobForm({ job, onClose, onSaved }) {
  const [form, setForm] = useState(job ? {
    title: job.title, description: job.description, location: job.location,
    department: job.department, employment_type: job.employment_type, is_active: job.is_active,
  } : {
    title: "", description: "", location: "Remote · India",
    department: "Engineering", employment_type: "Full-time", is_active: true,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (job) await authClient().patch(`/admin/jobs/${job.id}`, form);
      else await authClient().post("/admin/jobs", form);
      toast.success(job ? "Job updated" : "Job created");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8" data-testid="job-form">
        <div className="flex items-start justify-between p-6 border-b border-border sticky top-0 bg-card">
          <h3 className="font-display font-bold text-xl">{job ? "Edit job" : "Create job"}</h3>
          <button onClick={onClose} data-testid="close-job-form" className="p-2 hover:bg-accent rounded-md"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="job-title" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Department" value={form.department} onChange={(v) => setForm({ ...form, department: v })} testid="job-department" />
            <Input label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} required testid="job-location" />
          </div>
          <label className="block">
            <div className="label-xs mb-2">Employment Type</div>
            <select value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
              data-testid="job-type"
              style={{ colorScheme: "dark" }}
              className="w-full bg-background border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:border-ai">
              {["Full-time", "Part-time", "Contract", "Internship"].map((o) => <option key={o} className="bg-[#08090B] text-white">{o}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="label-xs mb-2">Description</div>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={8} required data-testid="job-description"
              className="w-full bg-background border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:border-ai resize-y" />
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="job-active" className="accent-ai w-4 h-4" />
            Active (visible on /careers)
          </label>
          <button type="submit" disabled={loading} data-testid="save-job-btn"
            className="w-full px-5 py-3 rounded-md bg-white text-background font-semibold hover:bg-white/90 transition-colors disabled:opacity-60">
            {loading ? "Saving…" : job ? "Save changes" : "Create job"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ApplicationsPanel() {
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    authClient().get("/admin/applications").then(r => setApps(r.data)).catch(() => toast.error("Could not load applications"));
  }, []);

  const downloadResume = async (appId, filename) => {
    try {
      const res = await authClient().get(`/admin/applications/${appId}/resume`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename || "resume"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="label-xs mb-1">Job Applications</div>
        <h2 className="font-display font-bold text-2xl">{apps.length} received</h2>
      </div>
      {apps.length === 0 ? (
        <div className="card-flat p-10 text-center text-muted-foreground">No applications yet.</div>
      ) : (
        <div className="card-flat overflow-x-auto" data-testid="admin-applications-list">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="border-b border-border">
                {["Name", "Email", "Role", "Reply status", "Date", ""].map((h, i) => (
                  <th key={i} className="text-left px-4 py-3 label-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} className="border-b border-border/60 hover:bg-accent/40 transition-colors">
                  <td className="px-4 py-3 font-medium">{a.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.email}</td>
                  <td className="px-4 py-3">{a.job_title}</td>
                  <td className="px-4 py-3"><EmailStatusPill status={a.applicant_email_status} /></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground num">{new Date(a.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="px-4 py-3 flex gap-2">
                    <button onClick={() => setSelected(a)} data-testid={`view-app-${a.id}`}
                      className="px-3 py-1.5 rounded-md border border-border text-xs hover:bg-accent">View</button>
                    <button onClick={() => downloadResume(a.id, a.resume_filename)} data-testid={`download-resume-${a.id}`}
                      className="px-3 py-1.5 rounded-md bg-ai text-white text-xs hover:bg-ai/90 flex items-center gap-1">
                      <Download className="w-3 h-3" /> Resume
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8" data-testid="app-detail">
            <div className="flex items-start justify-between p-6 border-b border-border sticky top-0 bg-card">
              <div>
                <div className="label-xs mb-1">Application</div>
                <h3 className="font-display font-bold text-xl">{selected.name}</h3>
                <div className="text-xs text-muted-foreground mt-1">{selected.job_title}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-accent rounded-md"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <Detail k="Email" v={selected.email} />
              <Detail k="Phone" v={selected.phone || "—"} />
              <Detail k="Applied" v={new Date(selected.created_at).toLocaleString("en-IN")} />
              <Detail k="Resume" v={`${selected.resume_filename} · ${(selected.resume_size / 1024).toFixed(0)} KB`} />
              <Detail k="Auto-reply" v={<EmailStatusPill status={selected.applicant_email_status} />} />
              <Detail k="Admin notif." v={<EmailStatusPill status={selected.admin_email_status} />} />
              <div>
                <div className="label-xs mb-2">Cover letter</div>
                <div className="p-4 rounded-md bg-background border border-border text-sm whitespace-pre-wrap leading-relaxed">
                  {selected.cover_letter || <span className="text-muted-foreground">— none provided —</span>}
                </div>
              </div>
              <button onClick={() => downloadResume(selected.id, selected.resume_filename)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ai text-white font-semibold hover:bg-ai/90">
                <Download className="w-4 h-4" /> Download Resume
              </button>
              <ReplyComposer application={selected} onSent={(app) => setSelected(app)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReplyComposer({ application, onSent }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(`Adviserz · ${application.job_title}`);
  const [body, setBody] = useState(`Hi ${application.name.split(" ")[0]},\n\nThanks again for applying. `);
  const [loading, setLoading] = useState(false);
  const replies = application.replies || [];

  const send = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authClient().post(`/admin/applications/${application.id}/reply`, { subject, body });
      toast.success("Reply sent");
      // refetch single app via list and pick this one
      const r = await authClient().get("/admin/applications");
      const updated = r.data.find((a) => a.id === application.id);
      if (updated) onSent(updated);
      setOpen(false);
      setBody(`Hi ${application.name.split(" ")[0]},\n\n`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reply");
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-4 mt-4 border-t border-border" data-testid="reply-composer">
      <div className="flex items-center justify-between mb-3">
        <div className="label-xs">Replies to applicant · {replies.length}</div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="open-reply-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-ai text-white text-xs font-semibold hover:bg-ai/90">
            <Send className="w-3.5 h-3.5" /> Reply to applicant
          </button>
        )}
      </div>

      {replies.length > 0 && (
        <ul className="space-y-2 mb-4" data-testid="replies-list">
          {replies.slice().reverse().map((r) => (
            <li key={r.id} className="p-3 rounded-md bg-background border border-border">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium truncate">{r.subject}</div>
                <EmailStatusPill status={r.status} />
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground num">{new Date(r.sent_at).toLocaleString("en-IN")}</div>
              <div className="mt-2 text-xs whitespace-pre-wrap text-muted-foreground line-clamp-3">{r.body}</div>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <form onSubmit={send} className="space-y-3 p-4 rounded-md bg-background border border-border">
          <label className="block">
            <div className="label-xs mb-2">Subject</div>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} required
              data-testid="reply-subject"
              className="w-full bg-card border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ai" />
          </label>
          <label className="block">
            <div className="label-xs mb-2">Message</div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={8}
              data-testid="reply-body"
              className="w-full bg-card border border-border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-ai resize-y" />
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} disabled={loading}
              className="px-3 py-2 rounded-md border border-border text-xs hover:bg-accent">Cancel</button>
            <button type="submit" disabled={loading} data-testid="reply-submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ai text-white text-sm font-semibold hover:bg-ai/90 disabled:opacity-60">
              <Send className="w-3.5 h-3.5" /> {loading ? "Sending…" : "Send reply"}
            </button>
          </div>
          <div className="text-[11px] text-muted-foreground">
            From: <span className="num">careers@adviserz.in</span> · To: <span className="num">{application.email}</span>
          </div>
        </form>
      )}
    </div>
  );
}

function EmailStatusPill({ status }) {
  if (!status || status.state === "not_sent") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-muted/30 text-muted-foreground text-[11px]">· not sent</span>;
  }
  if (status.state === "delivered") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-profit/20 text-profit text-[11px]"><Check className="w-3 h-3" /> delivered</span>;
  }
  if (status.state === "bounced") {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-risk/20 text-risk text-[11px]" title={status.bounce_type || ""}><AlertTriangle className="w-3 h-3" /> bounced{status.bounce_type ? ` · ${status.bounce_type}` : ""}</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-muted/30 text-muted-foreground text-[11px]" title="Email dispatched, awaiting delivery event"><Clock className="w-3 h-3" /> sent</span>;
}

function EmailEventsPanel() {
  const [data, setData] = useState({ events: [], counts: {}, total: 0, limit: 50, skip: 0, signature_verification: false });
  const [filter, setFilter] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 50;

  const fetchEvents = useCallback(() => {
    setLoading(true);
    const params = { limit: LIMIT, skip };
    if (filter) params.event_type = filter;
    authClient().get("/admin/email-events", { params })
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load events"))
      .finally(() => setLoading(false));
  }, [filter, skip]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const eventColor = (t) => t.includes("bounced") || t.includes("complained") ? "text-risk"
    : t.includes("delivered") || t.includes("opened") || t.includes("clicked") ? "text-profit"
    : "text-muted-foreground";

  const total = data.total || 0;
  const page = Math.floor(skip / LIMIT) + 1;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <div className="label-xs mb-1">Resend · Email events</div>
          <h2 className="font-display font-bold text-2xl" data-testid="email-events-total">{total} total</h2>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button onClick={fetchEvents} disabled={loading} data-testid="refresh-events-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border hover:bg-accent disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          {data.signature_verification ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-profit/20 text-profit"><ShieldCheck className="w-3 h-3" /> Signed webhooks verified</span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-yellow-500/20 text-yellow-300"><ShieldAlert className="w-3 h-3" /> Webhook secret not set</span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="email-events-counts">
        <FilterChip label="all" count={total} active={!filter} onClick={() => { setFilter(""); setSkip(0); }} />
        {Object.entries(data.counts || {}).map(([k, v]) => (
          <FilterChip key={k} label={k} count={v} active={filter === k} onClick={() => { setFilter(k); setSkip(0); }} />
        ))}
      </div>

      {data.events.length === 0 ? (
        <div className="card-flat p-10 text-center text-muted-foreground">
          {filter ? `No "${filter}" events yet.` : "No email events yet. Configure the webhook URL in Resend → Webhooks (see Next Actions in PRD)."}
        </div>
      ) : (
        <div className="card-flat overflow-x-auto" data-testid="email-events-list">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="border-b border-border">
                {["Event", "To", "Subject", "Email ID", "Received"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 label-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.events.map((e) => (
                <tr key={e.id} className="border-b border-border/60">
                  <td className={`px-4 py-3 font-mono text-xs ${eventColor(e.event_type)}`}>
                    {e.event_type}{e.bounce_type ? ` · ${e.bounce_type}` : ""}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{(e.to || []).join(", ") || "—"}</td>
                  <td className="px-4 py-3 truncate max-w-xs">{e.subject || "—"}</td>
                  <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">{e.email_id || "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground num">
                    {new Date(e.received_at).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground" data-testid="events-pagination">
          <div>Page <span className="num text-white">{page}</span> of <span className="num">{pages}</span></div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSkip(Math.max(0, skip - LIMIT))} disabled={skip === 0}
              data-testid="page-prev"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border hover:bg-accent disabled:opacity-40">
              <ChevronLeft className="w-3 h-3" /> Prev
            </button>
            <button onClick={() => setSkip(skip + LIMIT)} disabled={skip + LIMIT >= total}
              data-testid="page-next"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border hover:bg-accent disabled:opacity-40">
              Next <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, count, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`filter-${label}`}
      className={`px-3 py-1.5 rounded-sm border text-xs num transition-colors ${
        active ? "bg-ai/20 border-ai text-white" : "border-border text-muted-foreground hover:text-white hover:bg-accent"
      }`}>
      <span className="mr-2">{label}</span>
      <span className="text-white">{count}</span>
    </button>
  );
}

function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  useEffect(() => {
    authClient().get("/admin/leads").then(r => setLeads(r.data)).catch(() => toast.error("Could not load leads"));
  }, []);

  const exportCsv = () => {
    if (!leads.length) return;
    const rows = [["Name", "Email", "Experience", "Date"], ...leads.map(l => [l.name, l.email, l.experience, new Date(l.created_at).toISOString()])];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "adviserz-leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="label-xs mb-1">Early Access Leads</div>
          <h2 className="font-display font-bold text-2xl">{leads.length} captured</h2>
        </div>
        <button onClick={exportCsv} disabled={!leads.length} data-testid="export-leads-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border text-sm hover:bg-accent disabled:opacity-50">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      {leads.length === 0 ? (
        <div className="card-flat p-10 text-center text-muted-foreground">No leads yet.</div>
      ) : (
        <div className="card-flat overflow-x-auto" data-testid="admin-leads-list">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="border-b border-border">
                {["Name", "Email", "Experience", "Date"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 label-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-border/60">
                  <td className="px-4 py-3 font-medium">{l.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{l.email}</td>
                  <td className="px-4 py-3">{l.experience}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground num">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, required, testid, type = "text" }) {
  return (
    <label className="block">
      <div className="label-xs mb-2">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        required={required} data-testid={testid}
        className="w-full bg-background border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:border-ai" />
    </label>
  );
}

function Detail({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="label-xs">{k}</div>
      <div className="col-span-2 break-all">{v}</div>
    </div>
  );
}
